// Çapanın ekran üzerindeki yeri.
//
// Ölçüm, adımın kendisinden AYRI tutuluyor: bir adım "hangi eleman" der, bu
// kanca "nerede" der, Spotlight "nasıl çizilir" der. Üçü tek dosyada olsaydı
// her scroll olayı adım mantığını da yeniden çalıştırırdı.

import { useEffect, useState } from "react";

import { resolveAnchor } from "./anchors";

function scopeRoot(scope: "main" | "document"): ParentNode | null {
  if (scope === "document") return document;
  return document.querySelector("main");
}

export function useAnchorRect(
  anchorId: string | null,
  scope: "main" | "document",
): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (anchorId === null) {
      setRect(null);
      return undefined;
    }
    const root = scopeRoot(scope);
    const el = root === null ? null : resolveAnchor(anchorId, root);
    if (el === null) {
      setRect(null);
      return undefined;
    }

    // Görünmeyen bir elemanın etrafına halka çizmek, kullanıcıya boş bir ekran
    // gösterip "şuraya bak" demektir. happy-dom'da bu metot her elemanda
    // bulunmayabilir, ve turun bir test ortamı eksiği yüzünden patlaması
    // gösterdiği şeyden daha kötü olurdu.
    //
    // behavior "auto", "smooth" DEĞİL — ölçüldü: yumuşak kaydırma ilk ölçümden
    // SONRA bitiyor ve halka hedefin 14px altında kalıyordu. Anlık kaydırmada
    // hemen ardından yapılan ölçüm nihai konumu verir. Görsel yumuşaklık zaten
    // halkanın kendi CSS geçişinden geliyor, kaydırmadan değil.
    // "center" yalnız hedef ekrana SIĞARKEN doğru. Uzun bir listede ortalamak,
    // kullanıcıyı listenin ortasına düşürür: tanıtılan bölgenin başlangıcını
    // hiç görmez. Uzunsa başına hizala. (innerHeight sıfır olabilir — gizli
    // sekme ve happy-dom; o hâlde "uzun" kararı verilemez, eski davranış kalır.)
    const tall = window.innerHeight > 0 && el.getBoundingClientRect().height > window.innerHeight;
    el.scrollIntoView?.({ block: tall ? "start" : "center", behavior: "auto" });

    const measure = (): void => {
      setRect(el.getBoundingClientRect());
    };
    measure();

    // capture: true ZORUNLU. Kaydıran şey window değil, kabuğun `overflow-auto`
    // taşıyan <main>'i — ve scroll olayı BALONCUKLANMAZ. Capture aşaması, olayı
    // hedefe inerken window'da yakalayan tek yoldur; onsuz kutu sayfa kayarken
    // yerinde donar. Aynı sebeple `behavior: "smooth"` sonrası ilk ölçüm
    // kaymadan ÖNCEKİ konumu verir ve bu dinleyici onu düzeltir.
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);

    // Çapanın KENDİSİ büyüyebilir. Sol ray tam da bunu yapıyor: yetkiler
    // çözüldükçe menü öğeleri geliyor ve nav uzuyor — halka ilk ölçümde
    // donduğu için rayın yalnız üst yarısını çevreliyordu (tarayıcıda ölçüldü;
    // scroll/resize dinleyicileri bu değişimin hiçbirini görmez, çünkü ne
    // pencere kaydı ne de boyutu değişti).
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(el);

    return (): void => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [anchorId, scope]);

  return rect;
}
